import React from 'react';
import { Button } from '../ui/button';
import { Smartphone, Zap, PlayCircle, ArrowRight, Monitor, Tablet, CheckCircle } from 'lucide-react';
import PageLayout from '../layout/PageLayout';
import SEO from '../SEO';

const MobileTesting = ({ onBack, onLogin, onRegister }) => {
  return (
    <PageLayout onLogin={onLogin} onRegister={onRegister}>
      <SEO 
        title="Mobile Testing - AI-Powered Mobile App Automation | Lexa.run"
        description="Automated mobile testing for iOS and Android apps. Test your mobile applications with AI-powered automation."
      />

      <main className="container mx-auto px-4 sm:px-6 py-12 pt-24">
        {/* Hero Section */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Smartphone className="h-4 w-4 mr-2" />
            Mobile Testing
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            AI-Powered Mobile Testing
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Comprehensive mobile app testing for iOS and Android. Test real devices and emulators with natural language commands powered by AI.
          </p>
          <Button onClick={onRegister} size="lg" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white">
            <PlayCircle className="h-5 w-5 mr-2" />
            Start Testing Now
          </Button>
        </div>

        {/* Key Features */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">What You Can Test</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-4">
                <Smartphone className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Native Mobile Apps</h3>
              <p className="text-gray-600">Test native iOS and Android applications with full platform support and device capabilities.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Touch & Gestures</h3>
              <p className="text-gray-600">Test taps, swipes, pinch-to-zoom, long press, and complex multi-touch gestures automatically.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                <Monitor className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Screen Orientations</h3>
              <p className="text-gray-600">Validate app behavior in portrait and landscape modes with automatic rotation testing.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center mb-4">
                <Tablet className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Multiple Devices</h3>
              <p className="text-gray-600">Run tests across different screen sizes, resolutions, and device configurations simultaneously.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Permissions & Alerts</h3>
              <p className="text-gray-600">Handle system permissions, push notifications, and native alert dialogs seamlessly.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">App Performance</h3>
              <p className="text-gray-600">Monitor app performance, memory usage, and battery consumption during test execution.</p>
            </div>
          </div>
        </div>

        {/* Supported Platforms */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-12 mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Supported Platforms</h2>
          <p className="text-center text-gray-600 mb-8">Test on both major mobile platforms</p>
          <div className="grid grid-cols-2 gap-8 max-w-2xl mx-auto">
            <div className="bg-white p-8 rounded-xl text-center shadow-md">
              <div className="flex items-center justify-center mb-4">
                <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg" alt="iOS" className="h-20 w-20 object-contain" />
              </div>
              <h4 className="font-bold text-xl">iOS</h4>
              <p className="text-gray-600 mt-2">iPhone, iPad</p>
              <p className="text-sm text-gray-500 mt-1">iOS 12+</p>
            </div>
            <div className="bg-white p-8 rounded-xl text-center shadow-md">
              <div className="flex items-center justify-center mb-4">
                <img src="https://upload.wikimedia.org/wikipedia/commons/d/d7/Android_robot.svg" alt="Android" className="h-20 w-20 object-contain" />
              </div>
              <h4 className="font-bold text-xl">Android</h4>
              <p className="text-gray-600 mt-2">Phone, Tablet</p>
              <p className="text-sm text-gray-500 mt-1">Android 8+</p>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-bold mb-3">Select Device & Platform</h3>
              <p className="text-gray-600">Choose your target device, iOS or Android, along with the specific OS version and screen size.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-bold mb-3">Describe Your Test</h3>
              <p className="text-gray-600">Tell Lexa what to test using natural language. AI converts it into mobile test automation code.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-bold mb-3">Run & Analyze</h3>
              <p className="text-gray-600">Execute tests on real devices or emulators. Get detailed reports with screenshots and performance metrics.</p>
            </div>
          </div>
        </div>

        {/* Integrations Section - New Design */}
        <div className="mb-16 bg-white rounded-2xl p-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Fits into your existing workflow
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                With comprehensive integrations for <span className="text-blue-600 font-semibold">CI/CD</span>, <span className="text-blue-600 font-semibold">issue tracking</span>, and <span className="text-blue-600 font-semibold">test case management</span>, Lexa fits into your current development and testing process with minimal disruption.
              </p>
            </div>

            {/* Right Column - Integration Logos */}
            <div>
              <div className="grid grid-cols-3 gap-4">
                {/* Row 1 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/poehpjw0_image.png" alt="Azure DevOps" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/3/32/Atlassian_Bitbucket_Logo.png" alt="Bitbucket" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/tdtq4iim_image.png" alt="CircleCI" className="h-12 w-auto object-contain" />
                </div>

                {/* Row 2 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/9/91/Octicons-mark-github.svg" alt="GitHub" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/lbn2nq9z_image.png" alt="Discord" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/e/e1/GitLab_logo.svg" alt="GitLab" className="h-12 w-auto object-contain" />
                </div>

                {/* Row 3 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/r25r80p5_image.png" alt="Jenkins" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/hymy3wvl_image.png" alt="TeamCity" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/mc6i5gez_image.png" alt="Jira" className="h-12 w-auto object-contain" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Automate Your Mobile Testing?</h2>
          <p className="text-xl mb-8 opacity-90">Start testing in minutes, not days. No credit card required.</p>
          <Button onClick={onRegister} size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
            Start Free Trial
          </Button>
        </div>
      </main>
    </PageLayout>
  );
};

export default MobileTesting;
